import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { LuRefreshCw, LuTrash2 } from 'react-icons/lu';
import { Alert, Button, LoadingSpinner, Pagination } from 'components/common';
import { fetchOrphans, purgeOrphans } from './api';
import OrphanSummary from './OrphanSummary';
import OrphanTable from './OrphanTable';
import PurgeConfirmModal from './PurgeConfirmModal';

const PER_PAGE = 50;

// An orphaned attachment is a row in active_storage_attachments whose blob is
// gone: it shows up across the portal as a nameless, sizeless file. This screen
// is read-only until rows are ticked by hand — there is no purge-all, here or in
// the API, and the server re-checks every row at the moment of deletion.
const OrphanAttachments = () => {
  const [rows, setRows]       = useState([]);
  const [meta, setMeta]       = useState({ total: 0, total_pages: 1, groups: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const [page, setPage]             = useState(1);
  const [recordType, setRecordType] = useState('');
  const [name, setName]             = useState('');

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [preview, setPreview]         = useState(null);
  const [previewing, setPreviewing]   = useState(false);
  const [submitting, setSubmitting]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { rows: data, meta: info } = await fetchOrphans({ recordType, name, page, perPage: PER_PAGE });
      setRows(data);
      setMeta(info);
    } catch (e) {
      setError(e.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [recordType, name, page]);

  useEffect(() => { load(); }, [load]);

  // Selection is deliberately scoped to what is on screen: moving page or
  // changing the filter drops it, so nobody can delete rows they scrolled past
  // and can no longer see.
  useEffect(() => { setSelectedIds(new Set()); }, [page, recordType, name]);

  const toggle = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const togglePage = useCallback((ids, select) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (select ? next.add(id) : next.delete(id)));
      return next;
    });
  }, []);

  const selected = useMemo(() => Array.from(selectedIds), [selectedIds]);

  // Step one of two: ask the server what it *would* delete. The answer is what
  // the confirmation dialog shows, so the admin confirms a verified list rather
  // than their own selection.
  const openPreview = useCallback(async () => {
    setPreviewing(true);
    try {
      setPreview(await purgeOrphans({ ids: selected, dryRun: true }));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setPreviewing(false);
    }
  }, [selected]);

  const confirmPurge = useCallback(async () => {
    setSubmitting(true);
    try {
      const result = await purgeOrphans({ ids: preview?.deleted_ids || [], dryRun: false });
      const kept = result.skipped_count
        ? ` ${result.skipped_count} kept (no longer orphaned).`
        : '';
      toast.success(`Removed ${result.deleted_count} orphaned attachment row(s).${kept}`);
      setPreview(null);
      setSelectedIds(new Set());
      setPage(1);
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  }, [preview, load]);

  const pickGroup = useCallback((group) => {
    setRecordType(group.record_type || '');
    setName(group.name || '');
    setPage(1);
  }, []);

  const clearFilter = useCallback(() => {
    setRecordType('');
    setName('');
    setPage(1);
  }, []);

  return (
    <div className="tw-px-5 tw-py-5 tw-flex tw-flex-col tw-gap-4">

      <Alert
        type="info"
        title="Orphaned attachments"
        message="Rows whose stored file no longer exists. They render as a nameless, sizeless attachment and cannot be downloaded. Removing one deletes the database row only — the file is already gone. Reviewing is always safe; nothing is deleted until you select rows and confirm."
      />

      <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-flex-wrap">
        <div className="tw-text-[12.5px] tw-text-slate-500">
          {selected.length > 0
            ? `${selected.length} selected on this page`
            : 'Select rows to enable removal'}
        </div>

        <div className="tw-flex tw-items-center tw-gap-2">
          <Button variant="secondary" size="sm" leftIcon={LuRefreshCw} onClick={load} disabled={loading}>
            Refresh
          </Button>
          <Button
            variant="danger"
            size="sm"
            leftIcon={LuTrash2}
            onClick={openPreview}
            disabled={selected.length === 0 || previewing}
            loading={previewing}
          >
            Review removal
          </Button>
        </div>
      </div>

      {error && <Alert type="error" title="Could not load" message={error} />}

      <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-[320px_1fr] tw-gap-4 tw-items-start">
        <OrphanSummary
          total={meta.total || 0}
          groups={meta.groups || []}
          recordType={recordType}
          name={name}
          onPick={pickGroup}
          onClear={clearFilter}
        />

        <div className="tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-overflow-hidden">
          {loading ? (
            <div className="tw-py-12 tw-flex tw-justify-center"><LoadingSpinner /></div>
          ) : (
            <>
              <OrphanTable
                rows={rows}
                selectedIds={selectedIds}
                onToggle={toggle}
                onTogglePage={togglePage}
                loading={loading}
              />
              {(meta.total_pages || 1) > 1 && (
                <div className="tw-px-3 tw-py-2 tw-border-t tw-border-slate-100">
                  <Pagination
                    currentPage={page}
                    totalPages={meta.total_pages || 1}
                    totalResults={meta.total || 0}
                    perPage={meta.per_page || PER_PAGE}
                    onPageChange={setPage}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <PurgeConfirmModal
        show={Boolean(preview)}
        preview={preview}
        submitting={submitting}
        onHide={() => setPreview(null)}
        onConfirm={confirmPurge}
      />
    </div>
  );
};

export default OrphanAttachments;
